/**
 * ProjectDetailsTemplate Component
 *
 * Pure presentation component for the Project Details page.
 * Displays project information, chat input, AI hints, and conversation history.
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 40+ individual props (state, actions, handlers...)
 * After: 1 facade + 3-4 platform-specific props
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { CircleAlert, FileText, FolderPlus, ReplaceAll } from 'lucide-react';
import {
  addNewEvent,
  ConversationMode,
  EventType,
  type ProjectDetailsPageFacade,
  ProjectMode,
  useCreditLimitBanner,
} from '@ideoz/core';
import { googleDriveService } from '@ideoz/core/api/services/googleDrive.service';
import {
  Header,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Icon,
  showToast,
  QuickActionChips,
  type QuickAction,
} from '../index';
import { CreateFirstProjectDialog } from '../organisms/CreateFirstProjectDialog';
import UpgradeCtaCard from '../molecules/UpgradeCtaCard';
import { toast } from 'sonner';
import { AppLayout } from '../layouts/AppLayout';
import ChatTextAreaInternal from '../molecules/ChatTextArea';
import { ProjectConversationsList } from '../organisms/ProjectConversationsList';
import { ProjectDetailsModal } from '../organisms/ProjectDetailsModal';
import { UploadFilesDialog } from '../organisms/UploadFilesDialog';
import { useFileAttachments } from '../../hooks/useFileAttachments';
import GoogleFolderDialog from '../organisms/GoogleFolderDialog';
import DisconnectDialog from '../organisms/DisconnectDialog';
import DesignSystemDrawer from '../organisms/DesignSystemDrawer';
import SyncToast from '../organisms/SyncToast';
import { cn } from '@ideoz/ui';

/**
 * Project form data interface (for modal)
 */
export interface ProjectFormData {
  name: string;
  description: string;
  main_persona: string;
  design_process: string;
  ideoz_role: string;
  user_role: string;
  industry: string;
}

/**
 * ProjectDetailsTemplate Props
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 40+ individual props (state, actions, handlers...)
 * After: 1 facade + 4-5 platform-specific props
 *
 * The facade encapsulates all shared logic following useLandingPageFacade pattern:
 * - State (isLoggedIn)
 * - Header configuration & user menu
 * - Navigation (toLanding, toConversation, toProfile, etc.)
 * - Conversation actions (create, reset, etc.)
 * - Project Details (currentProject, conversations, attachments, drawer, etc.)
 */
export interface ProjectDetailsTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: ProjectDetailsPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Translation function
  t: (key: string) => string;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    isLoading: boolean;
    openRegister: () => void;
    openLogin: () => void;
    closeDialog: () => void;
    switchMode: (mode: 'login' | 'register') => void;
    handleLogin: (email: string, password: string) => Promise<void>;
    handleRegister: (name: string, email: string, password: string) => Promise<void>;
    handleGoogleAuth: () => Promise<void>;
  };

  // Plugin-specific
  pluginLogin?: any;
  pluginStopInterval?: () => void;
  isAuthLoading?: boolean;
}

/**
 * ProjectDetailsTemplate Component
 *
 * Displays project details page with:
 * - Header with project name
 * - Title section asking about problems
 * - Chat input area
 * - AI-generated prompt hints
 * - "Need smarter help?" section with action buttons
 * - Previous conversations list
 * - Modals for editing project and uploading files
 */
export const ProjectDetailsTemplate: React.FC<ProjectDetailsTemplateProps> = ({
  facade,
  isWeb,
  t = (key: string) => key,
  authDialogState,
  pluginLogin,
  pluginStopInterval,
  isAuthLoading = false,
}) => {
  // ========== Destructure Facade for Clean Usage ==========
  const {
    state: { isLoggedIn, currentMode, llmFamily, planDetails },
    header: {
      headerProps,
      userMenuItems = [],
      userMenuSocialLinks = [],
      isRequestingUpgrade = false,
    },
    navigation: {
      toLanding: onBack,
      toProjectsList: onNavigateToProjectsList,
      toConversation: onNavigateToConversation,
    },
    handleBack,
    conversation: {
      create: onCreateConversation,
      reset: onResetConversation,
      setLlmFamily: onSetLlmFamily,
      setName: onSetConversationName,
      setId: onSetConversationId,
      setMode: onSetConversationMode,
    },
    projectDetails: {
      currentProject,
      isLoading: isProjectLoading,
      projectMetaData,
      showProjectDetailsDrawer,
      setDrawerVisibility,
      conversations,
      isLoadingConversations,
      attachments,
      isLoadingAttachments,
      isUploadingFiles,
      fetchProject,
      updateProject: onUpdateProject,
      uploadFiles: onUploadFiles,
      deleteFile: onDeleteFile,
      handleDisconnectGoogleDrive: onDisconnectGoogleDrive,
    },
    sidebar,
    mode,
    firstProjectDialog: {
      show: showFirstProjectDialog,
      setShow: onSetShowFirstProjectDialog,
      onClose: onCloseFirstProjectDialog,
      onStart: onStartFirstProjectDialog,
    },
    brandSync,
  } = facade;

  // ========== Local State ==========
  const [isUploadFilesDialogOpen, setIsUploadFilesDialogOpen] = React.useState(false);
  const [isGoogleFolderDialogOpen, setIsGoogleFolderDialogOpen] = React.useState(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = React.useState(false);
  const [isDesignSystemDrawerOpen, setIsDesignSystemDrawerOpen] = React.useState(false);
  const [googleFolderStep, setGoogleFolderStep] = React.useState(1);
  const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
  const [connectedFolder, setConnectedFolder] = React.useState<string | null>(null);
  const [showSyncToast, setShowSyncToast] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState(0);
  const [totalFiles, setTotalFiles] = React.useState(0);
  const [isSyncToastExpanded, setIsSyncToastExpanded] = React.useState(true);
  const creditLimitBanner = useCreditLimitBanner();

  // ========== Refs for Interval Management ==========
  const syncIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSyncingRef = React.useRef<boolean>(false);
  const hasSeenProgressRef = React.useRef<boolean>(false); // Track if we've seen actual progress
  const zeroProgressCountRef = React.useRef<number>(0); // Count how many times we get 0/0

  // ========== Custom Hooks ==========
  // Scroll the main content area to top on mode change
  const mainScrollRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    setTimeout(() => {
      if (mainScrollRef.current) {
        mainScrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 0);
  }, [currentMode]);
  // ========== File Attachments Hook ==========
  const fileAttachmentsHook = useFileAttachments({
    maxFiles: 10,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxTotalSize: 50 * 1024 * 1024, // 50MB
    allowedFileTypes: ['.pdf', '.txt', '.doc', '.docx', '.png', '.jpg', '.jpeg'],
  });

  // ========== Cleanup Effect ==========
  React.useEffect(() => {
    // Cleanup function: clear intervals and timeouts on unmount
    return () => {
      isSyncingRef.current = false;
      hasSeenProgressRef.current = false;
      zeroProgressCountRef.current = 0;
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    // Sync connectedFolder state with currentProject data
    if (currentProject?.gdriveFolderId) {
      setConnectedFolder(currentProject.gdriveFolderName || null);
    } else {
      setConnectedFolder(null);
    }
  }, [currentProject?.gdriveFolderId, currentProject?.gdriveFolderName]);

  // ========== Computed Values ==========
  const projectName = currentProject?.name || currentProject?.title || 'Project';
  const projectId = currentProject?.id || currentProject?.project_id;

  // Mode-based content
  const modeContent = React.useMemo(() => {
    if (currentMode === 'prototyper') {
      return {
        title: 'Start Prototyping, transform Your problem into possibility',
        description:
          'Describe your solution. Ideoz AI uses the context to turn great ideas into reality, fast',
        placeholder: 'Describe your UX experience prototype to generate',
      };
    }
    return {
      title: 'Exit the hype, focus on the right problem first',
      description: 'Uncover the right UX problem faster and smarter with Ideoz-AI',
      placeholder: 'Uncover the right UX problem faster and smarter with Ideoz-AI',
    };
  }, [currentMode, projectName]);

  // ========== Event Handlers ==========

  /**
   * Handle chat text area submit
   */
  const handleSubmit = (text: string, attachmentIds: string[], llmFamily?: string) => {
    if (!text.trim() && attachmentIds.length === 0) return;

    try {
      addNewEvent(EventType.OnUserMessage, {
        conversationId: '',
        location: 'projectDetailsPage',
        projectId: projectId || null,
        userId: currentProject?.user_id || null,
      }).catch(() => {
        /* silent */
      });
    } catch {
      /* ignore analytics errors */
    }

    onResetConversation();
    onCreateConversation({
      init_message: text,
      mode: currentMode,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      llmFamily: llmFamily,
    });

    onNavigateToConversation();
  };

  /**
   * Handle quick action chip click
   */
  const handleQuickActionClick = (action: QuickAction) => {
    try {
      addNewEvent(EventType.OnUserMessage, {
        conversationId: '',
        location: 'projectDetailsPage',
        projectId: projectId || null,
        userId: currentProject?.user_id || null,
      }).catch(() => {
        /* silent */
      });
    } catch {
      /* ignore analytics errors */
    }

    onResetConversation();
    onCreateConversation({
      init_message: action.prompt,
      mode: currentMode,
      skip_analysis: true,
    });

    onNavigateToConversation();
  };

  /**
   * Handle project update
   */
  const handleUpdateProject = async (data: ProjectFormData) => {
    try {
      await onUpdateProject(data);
      // Success toast handled by platform page
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  };

  /**
   * Handle files upload
   */
  const handleFilesUpload = async (files: File[]) => {
    try {
      await onUploadFiles(files);
      addNewEvent(EventType.AfterFilesUploaded, { location: 'project chat' });
    } catch (error) {
      console.error('Failed to upload files:', error);
      throw error;
    }
  };

  /**
   * Handle file delete
   */
  const handleFileDelete = async (fileId: string) => {
    try {
      await onDeleteFile(fileId);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  };

  /**
   * Handle conversation click
   */
  const handleConversationClick = (
    conversationId: string,
    conversationTitle?: string,
    conversationMode?: ConversationMode
  ) => {
    onResetConversation();
    onSetConversationName(conversationTitle || 'New Conversation');
    onSetConversationId(conversationId);
    onSetConversationMode(conversationMode || currentMode);
    onNavigateToConversation(conversationId);
  };

  /**
   * Handle allow access from GoogleFolderDialog organism.
   * Runs template-owned side-effects: close dialog, connect folder and start real sync.
   */
  const handleAllowAccess = async (folderId: string) => {
    if (!projectId || !folderId) {
      console.error('❌ Missing projectId or folderId');
      toast.error('Unable to connect folder. Please try again.');
      return;
    }

    try {
      // Step 1: Set the folder for the project
      await googleDriveService.setProjectFolder(projectId, folderId);

      // Step 2: Refetch project to update state with new folder info
      await fetchProject(projectId);

      // Step 3: Trigger sync (fire-and-forget)
      googleDriveService.syncProjectFolder(projectId);

      // Close the dialog
      setIsGoogleFolderDialogOpen(false);
      setGoogleFolderStep(1);
      // Note: connectedFolder will be updated by the useEffect that watches currentProject

      // Don't show sync toast yet - wait for actual progress data
      setShowSyncToast(false);
      setSyncProgress(0);
      setTotalFiles(0);

      // Clear any existing polling interval and timeout
      isSyncingRef.current = false;
      hasSeenProgressRef.current = false; // Reset progress tracking
      zeroProgressCountRef.current = 0; // Reset 0/0 counter
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }

      // Set syncing flag
      isSyncingRef.current = true;

      // Helper function to fetch and update sync progress
      const fetchSyncProgress = async () => {
        // Check flag first - if not syncing, don't make API call
        if (!isSyncingRef.current) {
          return false;
        }

        try {
          const progressData = await googleDriveService.getSyncProgress(projectId);

          // Check flag again after async call returns
          if (!isSyncingRef.current) {
            return false;
          }

          // Check if progress is null - means no sync in progress yet
          if (progressData.progress === null) {
            // Don't show popup but keep polling
            return false; // Continue polling
          }

          // Parse the "current/total" format (e.g., "5/10" or "10/10")
          const progressString = progressData.progress || '0/0';
          const [currentStr, totalStr] = progressString.split('/');
          const current = parseInt(currentStr, 10) || 0;
          const total = parseInt(totalStr, 10) || 0;

          // Show the sync toast once we have actual progress data
          if (!showSyncToast && (total > 0 || (total === 0 && current === 0))) {
            setShowSyncToast(true);
          }

          // Track if we've seen actual progress (total > 0)
          if (total > 0) {
            hasSeenProgressRef.current = true;
            zeroProgressCountRef.current = 0; // Reset counter when we see real progress

            // Only update state when we have valid progress (total > 0)
            setSyncProgress(current);
            setTotalFiles(total);
          } else if (total === 0 && current === 0) {
            // Increment counter when we get 0/0
            zeroProgressCountRef.current += 1;
          }

          // Check if sync is complete
          // Case 1: We've seen progress and current === total (normal completion)
          // Case 2: We've received 0/0 for 5+ consecutive times (empty folder or already synced)
          const hasRealCompletion = current === total && hasSeenProgressRef.current;
          const hasEmptyFolderCompletion =
            total === 0 && current === 0 && zeroProgressCountRef.current >= 5;

          if (hasRealCompletion || hasEmptyFolderCompletion) {
            // CRITICAL: Set flag to false FIRST (synchronous) before any other operation
            isSyncingRef.current = false;

            // Clear the polling interval IMMEDIATELY
            const intervalId = syncIntervalRef.current;
            syncIntervalRef.current = null;
            if (intervalId) {
              clearInterval(intervalId);
            }

            // Clear the safety timeout
            const timeoutId = safetyTimeoutRef.current;
            safetyTimeoutRef.current = null;
            if (timeoutId) {
              clearTimeout(timeoutId);
            }

            // Keep toast visible for 2 seconds to show completion
            setTimeout(() => {
              setShowSyncToast(false);
              setSelectedFolder(null);
            }, 2000);

            return true; // Sync completed
          }

          return false; // Sync still in progress
        } catch (error) {
          return false; // Don't stop on error, keep polling
        }
      };

      // Step 4: Fetch initial progress immediately
      fetchSyncProgress().then((completed) => {
        // Only start interval if sync not already completed
        if (!completed && isSyncingRef.current) {
          syncIntervalRef.current = setInterval(fetchSyncProgress, 1000); // Poll every second

          // Safety timeout: stop polling after 10 minutes
          safetyTimeoutRef.current = setTimeout(() => {
            isSyncingRef.current = false;
            if (syncIntervalRef.current) {
              clearInterval(syncIntervalRef.current);
              syncIntervalRef.current = null;
            }
          }, 600000);
        }
      });
    } catch (error: any) {
      console.error('❌ Error during folder access:', error);
      toast.error(error?.message || 'Failed to connect folder. Please try again.');

      // Reset state on error
      setIsGoogleFolderDialogOpen(false);
      setGoogleFolderStep(1);
    }
  };

  // Google Icon

  const googleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width="28"
      height="28"
      viewBox="0 0 48 48"
    >
      <path fill="#FFC107" d="M17 6L31 6 45 30 31 30z"></path>
      <path fill="#1976D2" d="M9.875 42L16.938 30 45 30 38 42z"></path>
      <path fill="#4CAF50" d="M3 30.125L9.875 42 24 18 17 6z"></path>
    </svg>
  );

  const driveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 13" fill="none">
      <path
        d="M6.8757 4.03217L4.71265 0.214845C4.78447 0.141723 4.8671 0.0937604 4.94973 0.0615234C4.31186 0.271457 4.01917 0.987745 4.01917 0.987745L0.0676074 7.95957C0.0120059 8.17815 -0.00344032 8.37551 0.00350987 8.54927H4.31649L6.8757 4.03217Z"
        fill="#34A853"
      />
      <path
        d="M6.87695 4.03217L9.43617 8.54927H13.7491C13.7561 8.37551 13.7406 8.17815 13.685 7.95957L9.73348 0.987745C9.73348 0.987745 9.44003 0.271457 8.80293 0.0615234C8.88479 0.0937604 8.96819 0.141723 9.04001 0.214845L6.87695 4.03217Z"
        fill="#FBBC05"
      />
      <path
        d="M6.87594 4.03214L9.039 0.214816C8.96718 0.141693 8.88378 0.0937309 8.80192 0.061494C8.70771 0.0308296 8.60654 0.0103867 8.49688 0.00488281H8.38259H5.3693H5.255C5.14612 0.00960041 5.04418 0.0300433 4.94997 0.061494C4.86811 0.0937309 4.78471 0.141693 4.71289 0.214816L6.87594 4.03214Z"
        fill="#188038"
      />
      <path
        d="M4.31764 8.54883L2.1793 12.3229C2.1793 12.3229 2.10825 12.2883 2.01172 12.2168C2.31521 12.4503 2.61021 12.4998 2.61021 12.4998H11.0084C11.4717 12.4998 11.5682 12.3229 11.5682 12.3229C11.5698 12.3221 11.5705 12.3213 11.5721 12.3205L9.4353 8.54883H4.31764Z"
        fill="#4285F4"
      />
      <path
        d="M4.31884 8.54883H0.00585938C0.0274822 9.06147 0.249889 9.36104 0.249889 9.36104L0.41206 9.64253C0.423644 9.65904 0.430593 9.66847 0.430593 9.66847L0.783509 10.288L1.57583 11.6672C1.599 11.723 1.62603 11.7741 1.6546 11.8228C1.66541 11.8394 1.67545 11.8574 1.68703 11.8732C1.69012 11.8779 1.69321 11.8826 1.6963 11.8873C1.79437 12.0257 1.90403 12.1326 2.01369 12.2168C2.11022 12.2891 2.18127 12.3229 2.18127 12.3229L4.31884 8.54883Z"
        fill="#1967D2"
      />
      <path
        d="M9.43555 8.54883H13.7485C13.7269 9.06147 13.5045 9.36104 13.5045 9.36104L13.3423 9.64253C13.3307 9.65904 13.3238 9.66847 13.3238 9.66847L12.9709 10.288L12.1786 11.6672C12.1554 11.723 12.1284 11.7741 12.0998 11.8228C12.089 11.8394 12.0789 11.8574 12.0673 11.8732C12.0643 11.8779 12.0612 11.8826 12.0581 11.8873C11.96 12.0257 11.8503 12.1326 11.7407 12.2168C11.6442 12.2891 11.5731 12.3229 11.5731 12.3229L9.43555 8.54883Z"
        fill="#EA4335"
      />
    </svg>
  );

  // ========== Header Menu Items ==========
  const headerMenuItems = React.useMemo(
    () => [
      {
        id: 'upload-files',
        label: 'Upload files',
        icon: FolderPlus as any,
        onClick: () => setIsUploadFilesDialogOpen(true),
      },
      {
        id: 'update-project-context',
        label: 'Update project context',
        icon: FileText as any,
        onClick: () => setDrawerVisibility(true),
      },
      {
        id: 'brand-sync',
        label: 'Brand Sync',
        icon: ReplaceAll as any,
        description: 'Make the generated prototype look like your brand identity.',
        disabled: !brandSync.isEnabled,
        // Only show submenu (tooltip) when disabled
        submenu: !brandSync.isEnabled ? {
          title: 'Brand Sync',
          subtitle: 'Paid plan feature',
          description: 'Brand Sync allows you to update project design system to make the prototype match your brand identity.',
          actionLabel: 'Upgrade',
          onAction: brandSync.onUpgrade,
        } : undefined,
        // When enabled, open Design System Drawer (same as "Update your design system")
        onClick: brandSync.isEnabled ? () => setIsDesignSystemDrawerOpen(true) : () => { },
      },
      {
        id: 'link-google-folder',
        label: connectedFolder ? 'Link project google folder' : 'Link project google folder',
        icon: driveIcon as any,
        description: 'Connect your Google Drive with the project to easier update the context',
        badge: connectedFolder ? 'Connected - ' + connectedFolder : undefined,
        onClick: () => {
          if (connectedFolder) {
            setIsDisconnectDialogOpen(true);
          } else {
            setIsGoogleFolderDialogOpen(true);
          }
        },
      },
    ],
    [setDrawerVisibility, connectedFolder, brandSync, facade.navigation]
  );

  // ========== Render: Loading State ==========
  if (!currentProject && isProjectLoading) {
    return (
      <div className="h-[100vh] flex flex-col">
        <Header showBackButton backText="Loading..." onBack={onBack} isHaderMenu />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Loading project context...</p>
        </main>
      </div>
    );
  }

  // // ========== Render: Not Found State ==========
  // if (!currentProject && !isProjectLoading) {
  //   return (
  //     <div className="min-h-screen flex flex-col">
  //       <Header
  //         showBackButton
  //         backText="Project Not Found"
  //         onBack={onBack}
  //       />
  //       <main className="flex-1 flex items-center justify-center">
  //         <p className="text-gray-600">Project not found. Redirecting to landing page...</p>
  //       </main>
  //     </div>
  //   );
  // }

  // Prototyper Limitation: disable quick actions in prototyper mode
  const isPrototyperMode = currentMode === 'prototyper';

  // Check if user has credits remaining
  // Base credits are ALWAYS required first
  // In prototyper mode: also check prototype credits
  const prototypeCredit = planDetails?.credits?.find((c: any) => c.type === 'prototype');
  const prototypeBalance = prototypeCredit != null ? prototypeCredit.balance : 1; // If no prototype credit in planDetails, allow (don't block)

  const baseCredit = planDetails?.credits?.find((c: any) => c.type === 'base');
  const baseBalance = baseCredit != null
    ? (typeof baseCredit.balance === 'string' ? parseInt(baseCredit.balance, 10) : (baseCredit.balance ?? 0))
    : 1; // If no base credit in planDetails, allow (don't block)

  // Base credits are always required first
  const hasBaseCredits = baseBalance > 0;
  const hasPrototypeCredits = prototypeBalance > 0;

  // Final check: base credits required, then prototype credits in prototyper mode
  // If no base credits -> block (show UpgradeCtaCard)
  // If has base credits AND in prototyper mode -> also check prototype credits
  const canProceed = hasBaseCredits && (isPrototyperMode ? hasPrototypeCredits : true);

  // ========== Render: Main Content ==========
  return (
    <AppLayout
      sidebar={sidebar}
      user={{
        isLoggedin: isLoggedIn,
        name: headerProps.user?.name,
        avatar: headerProps.user?.avatar,
      }}
      userMenuProps={{
        points: headerProps.points,
        pointsLabel: 'Points',
        menuItems: userMenuItems,
        socialLinks: userMenuSocialLinks,
        isRequestingUpgrade,
        canRequestUpgrade: true,
        isWeb,
      }}
      isWeb={isWeb}
      height="full"
      mainClassName="h-[100vh]"
      mainScrollRef={mainScrollRef}
    >
      <Header
        {...headerProps}
        showBackButton
        backText={projectName}
        onBack={handleBack}
        isHaderMenu
        isHasBorder
        menuItems={headerMenuItems}
        isWeb={isWeb}
        creditLimitBanner={
          creditLimitBanner.isVisible
            ? {
              ...creditLimitBanner,
              onAction: () => facade.navigation.toBilling(),
              onDismiss: () => creditLimitBanner.setVisible(false),
            }
            : undefined
        }
      />

      <div
        className={`max-w-landing-page mx-auto py-20 lg:py-36 ${isWeb ? 'flex flex-col justify-center' : ''}`}
      >
        {/* Title Section */}
        <div className="flex flex-col gap-1">
          <h2
            className={`text-gray-800 text-center leading-tight text-3xl font-medium}`}
            data-testid="project-title-heading"
          >
            {modeContent.title}
          </h2>
          <p
            className={`text-gray-600 text-center leading-tight text-base`}
            data-testid="project-title-description"
          >
            {modeContent.description}
          </p>
        </div>

        {/* Chat Text Area or Upgrade CTA (prototyper/free plan limit or out of credits) */}
        <div className="mt-8 mb-8 flex justify-center">
          {canProceed ? (
            <ChatTextAreaInternal
              placeholder={modeContent.placeholder}
              onSubmit={handleSubmit}
              useFileAttachmentsHook={useFileAttachments}
              isWeb={isWeb}
              analyticsContext={projectId ? { projectId, isConversation: false } : undefined}
              initialLlmFamily={llmFamily as any}
              onLlmFamilyChange={(newModel) => {
                // Update Redux state when model changes
                onSetLlmFamily(newModel);
              }}
            />
          ) : (
            <div className="w-full flex flex-col items-center justify-center">
              <UpgradeCtaCard
                className='w-11/12 rounded-lg'
                title={!hasBaseCredits ? "You've used all your credits" : "You've used all your prototype credits"}
                description={!hasBaseCredits ? "Upgrade your plan or purchase additional credits to continue." : "Upgrade your plan or purchase additional prototype credits to continue."}
                buttonText="Upgrade"
                onUpgrade={facade.navigation.toBilling}
                data-testid="upgrade-cta-prototyper"
              />
            </div>
          )}
        </div>

        {/* Quick Action Chips - Only in uncover mode */}
        {currentMode === 'uncover' && (
          <div className="mb-16" data-testid="quick-action-chips-section">
            <QuickActionChips
              onActionClick={handleQuickActionClick}
              isWeb={isWeb}
              testId="project-quick-actions"
              disabled={baseBalance === 0}
            />
          </div>
        )}

        {/* Need Smarter Help Section */}
        {(!currentProject?.hasUpdateDetails ||
          !currentProject?.hasAddAttachment ||
          !connectedFolder) && (
            <div className="flex flex-col gap-4" data-testid="need-smarter-help-section">
              <p className="text-gray-800 text-start text-base font-medium leading-[120%]">
                Need smarter help?
              </p>
              <div className="flex flex-row lg:items-center gap-4">
                {!currentProject?.hasUpdateDetails && (
                  <Button
                    variant="secondary-gray"
                    size="default"
                    onClick={() => setDrawerVisibility(true)}
                    className="px-3 py-2"
                    data-testid="btn-update-project-context"
                  >
                    <Icon icon={FileText} size={18} strokeWidth={2} color="#414651" />
                    Update project context
                  </Button>
                )}
                {!currentProject?.hasAddAttachment && (
                  <Button
                    variant="secondary-gray"
                    size="default"
                    onClick={() => setIsUploadFilesDialogOpen(true)}
                    className="px-3 py-2"
                    data-testid="btn-upload-project-files"
                  >
                    <Icon icon={FolderPlus} size={18} color="#414651" strokeWidth={2} />
                    Upload project files
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <Icon icon={CircleAlert} size={18} color="#414651" strokeWidth={2} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-semibold">Supported files <span className="font-normal">(Maximum file size 10 MB)</span></p>
                          <p>JPG, PNG, PDF, DOC, DOCX, TXT, MD, PPT, PPTX, XLS, XLSX</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Button>
                )}
                {!connectedFolder && (
                  <Button
                    variant="secondary-gray"
                    size="default"
                    onClick={() => setIsGoogleFolderDialogOpen(true)}
                    className="px-3 py-2"
                    data-testid="btn-link-google-folder"
                  >
                    <Icon icon={driveIcon} size={20} strokeWidth={2} color="#414651" />
                    Link Google Drive folder
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <Icon icon={CircleAlert} size={18} color="#414651" strokeWidth={2} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-semibold">Supported files <span className="font-normal">(Maximum file size 10 MB)</span></p>
                          <p>JPG, PNG, PDF, DOC, DOCX, TXT, MD, PPT, PPTX, XLS, XLSX</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Button>
                )}
              </div>
            </div>
          )}

        {/* Previous Conversations List */}
        <ProjectConversationsList
          conversations={conversations}
          isLoading={isLoadingConversations}
          hasMore={false}
          onLoadMore={() => { }}
          onConversationClick={handleConversationClick}
        />
      </div>

      {/* Modal Components */}
      <ProjectDetailsModal
        isOpen={showProjectDetailsDrawer}
        onDrawerClose={() => setDrawerVisibility(false)}
        onUpdate={onUpdateProject}
        isUpdating={false}
        projectId={projectId}
        initialValues={{
          name: currentProject?.name || currentProject?.title || projectMetaData?.title || '',
          description: currentProject?.description || projectMetaData?.description || '',
          main_persona: currentProject?.mainPersona || projectMetaData?.persona || '',
          design_process: currentProject?.designProcess || 'double_diamond',
          ideoz_role: currentProject?.ideozRole || projectMetaData?.ideoz_role || '',
          user_role: currentProject?.userRole || projectMetaData?.user_role || '',
          industry: currentProject?.industry || projectMetaData?.industry || '',
        }}
        isWeb={isWeb}
      />
      <UploadFilesDialog
        isOpen={isUploadFilesDialogOpen}
        onClose={() => setIsUploadFilesDialogOpen(false)}
        projectAttachments={attachments.map((att: any) => ({
          id: att.id,
          fileName: att.file_name || att.fileName || att.name,
          isLoading: false,
        }))}
        onFilesUpload={onUploadFiles}
        onFileDelete={handleFileDelete}
        isUploading={isUploadingFiles}
        loadingAttachments={isLoadingAttachments}
        showToast={showToast}
        t={t}
        isWeb={isWeb}
        planDetails={planDetails}
      />

      {/* Google Folder Dialog (organism) */}
      {isWeb && (
        <GoogleFolderDialog
          open={isGoogleFolderDialogOpen}
          step={googleFolderStep}
          selectedFolder={selectedFolder}
          onStepChange={setGoogleFolderStep}
          onClose={() => {
            setIsGoogleFolderDialogOpen(false);
            setGoogleFolderStep(1);
          }}
          onSelectFolder={(name: string) => setSelectedFolder(name)}
          onAllowAccess={handleAllowAccess}
          folders={[]}
        />
      )}

      {/* Disconnect Dialog (organism) */}
      <DisconnectDialog
        open={isDisconnectDialogOpen}
        connectedFolder={connectedFolder}
        onClose={() => setIsDisconnectDialogOpen(false)}
        onDisconnect={async () => {
          // Disconnect Google Drive folder and update project state
          await onDisconnectGoogleDrive();

          // Update local UI state
          setConnectedFolder(null);
          setIsDisconnectDialogOpen(false);
        }}
      />

      {/* Design System Drawer (organism) */}
      <DesignSystemDrawer
        open={isDesignSystemDrawerOpen}
        projectId={projectId}
        onClose={() => setIsDesignSystemDrawerOpen(false)}
        onUpdated={(payload) => {
          // notify or refresh data as needed; for now we'll log and close drawer
          console.log('Design system updated', payload);
        }}
      />

      {/* First Project Welcome Dialog */}
      <CreateFirstProjectDialog
        open={false} // should be showFirstProjectDialog
        onOpenChange={onSetShowFirstProjectDialog}
        onStart={onStartFirstProjectDialog}
      />

      <SyncToast
        open={showSyncToast}
        progress={syncProgress}
        total={totalFiles}
        expanded={isSyncToastExpanded}
        onClose={() => {
          setShowSyncToast(false);
          setSelectedFolder(null);
          setIsSyncToastExpanded(true);
        }}
        onToggleExpand={(val) => setIsSyncToastExpanded(val)}
      />
    </AppLayout>
  );
};

export default ProjectDetailsTemplate;
